/**
 * PathwayBandChart — visual P10/P50/P90 confidence-band comparison
 * (R-131-FIN-DECISION-PWIN / band-visualisation).
 *
 * Replaces the today's TEXT-ONLY "p10..p90" cell in PathwayCompareCard with an
 * actual chart: each pathway is a column with its DETERMINISTIC point estimate
 * (authoritative `lcoeRealUsdPerMwh` / `npvUsd`) and a P10..P90 range bar drawn
 * from the EXISTING Monte-Carlo band fields already on PathwayCompareMetrics
 * (`lcoeP10`/`lcoeP90`, `npvP10`/`npvP90`). No new numbers are fabricated — the
 * chart only renders metrics the comparison engine already produced.
 *
 * Bars are coloured by pathway `classification` (btm / grid / hybrid). The
 * component is accessible (per-pathway aria labels + an off-screen data table)
 * and responsive (SafeResponsiveContainer, the repo's measured-mount wrapper).
 */

import React, { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ErrorBar,
  ReferenceLine,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { SafeResponsiveContainer } from '../../ui/SafeResponsiveContainer';
import type { PathwayClassification } from '../../../lib/finDecision/types';

/** Minimal shape this chart consumes — a structural subset of PathwayCompareMetrics. */
export interface PathwayBandDatum {
  pathwayId: string;
  /** Short display label for the column. */
  label: string;
  classification: PathwayClassification;
  /** Authoritative deterministic LCOE point (USD/MWh). */
  lcoeRealUsdPerMwh: number;
  lcoeP10: number;
  lcoeP90: number;
  /** Authoritative deterministic NPV point (USD). */
  npvUsd: number;
  npvP10: number;
  npvP90: number;
}

export type BandMetric = 'lcoe' | 'npv';

export interface PathwayBandChartProps {
  data: PathwayBandDatum[];
  /** Controlled metric (LCOE vs NPV). When omitted the chart owns the toggle. */
  metric?: BandMetric;
  onMetricChange?: (metric: BandMetric) => void;
  /** Override chart height in px (default 256). */
  heightPx?: number;
}

/**
 * Classification → fill colour. These mirror the categorical palette already in
 * use by sibling financial charts (PathwayCashflowChart OVERLAY_COLOR_VARS /
 * PathwayRiskMatrix DOT_COLOR_VARS) so the comparison surface stays visually
 * consistent. Aurora chart tokens (var(--chart-*)) flip with light/dark so the
 * bars read on both the dark app and the light theme. NOT data — purely
 * presentational.
 */
const CLASSIFICATION_COLORS: Record<PathwayClassification, string> = {
  btm: 'var(--chart-1)', // teal — behind-the-meter generation
  grid: 'var(--chart-5)', // green — grid tariff
  hybrid: 'var(--chart-3)', // violet — bridge / mixed
};

const CLASSIFICATION_LABEL: Record<PathwayClassification, string> = {
  btm: 'Behind-the-meter',
  grid: 'Grid tariff',
  hybrid: 'Hybrid bridge',
};

function isFiniteNum(v: number): boolean {
  return Number.isFinite(v);
}

function formatLcoe(v: number): string {
  if (!isFiniteNum(v)) return '—';
  return `$${Math.round(v)}/MWh`;
}

function formatNpv(v: number): string {
  if (!isFiniteNum(v)) return '—';
  const millions = v / 1_000_000;
  const sign = millions >= 0 ? '+' : '−';
  return `${sign}$${Math.abs(millions).toFixed(0)}M`;
}

interface ChartRow {
  pathwayId: string;
  label: string;
  classification: PathwayClassification;
  /** Centre point = the authoritative deterministic estimate. */
  point: number;
  /**
   * Asymmetric error offsets relative to `point`: [below, above]. Recharts'
   * ErrorBar draws point−below .. point+above. We derive these from the P10/P90
   * band so the whisker spans exactly P10..P90 around the deterministic point.
   */
  error: [number, number];
  p10: number;
  p90: number;
}

/**
 * Build chart rows for the selected metric. Rows whose point estimate is
 * non-finite (NaN sentinel from a non-converging cashflow) are DROPPED so the
 * axis never auto-scales to NaN and no "NaN" text is rendered.
 */
function buildRows(data: PathwayBandDatum[], metric: BandMetric): ChartRow[] {
  return data.flatMap((d) => {
    const point = metric === 'lcoe' ? d.lcoeRealUsdPerMwh : d.npvUsd;
    const p10 = metric === 'lcoe' ? d.lcoeP10 : d.npvP10;
    const p90 = metric === 'lcoe' ? d.lcoeP90 : d.npvP90;
    if (!isFiniteNum(point)) return [];
    // Guard the band: if a percentile is non-finite, collapse that side of the
    // whisker to the point (zero-length) rather than emitting NaN.
    const lo = isFiniteNum(p10) ? p10 : point;
    const hi = isFiniteNum(p90) ? p90 : point;
    // ErrorBar offsets are non-negative magnitudes from the point.
    const below = Math.max(0, point - Math.min(lo, hi));
    const above = Math.max(0, Math.max(lo, hi) - point);
    return [
      {
        pathwayId: d.pathwayId,
        label: d.label,
        classification: d.classification,
        point,
        error: [below, above] as [number, number],
        p10: Math.min(lo, hi),
        p90: Math.max(lo, hi),
      },
    ];
  });
}

function fmt(metric: BandMetric, v: number): string {
  return metric === 'lcoe' ? formatLcoe(v) : formatNpv(v);
}

const TOGGLE_OPTIONS: ReadonlyArray<{ value: BandMetric; label: string }> = [
  { value: 'lcoe', label: 'LCOE band' },
  { value: 'npv', label: 'NPV band' },
];

/**
 * Custom tooltip content. The default Recharts tooltip derives each ROW's text
 * colour from its series colour; the point series here uses a custom Scatter
 * `shape`, so its tooltip item colour is undefined and falls back to near-black —
 * invisible once the box is themed dark. It also surfaced three noisy rows for a
 * single value (Bar "LCOE", Scatter "Point estimate", and a spurious "label —").
 * Rendering our own content reads the ChartRow straight off the payload and emits
 * one clean, high-contrast block using Aurora tokens that flip dark/light.
 */
function BandTooltip({
  active,
  payload,
  metric,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ChartRow }>;
  metric: BandMetric;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div
      className="rounded-md border border-border bg-surface-1 px-3 py-2 shadow-lg"
      data-testid="pathway-band-tooltip"
    >
      <div className="type-meta font-semibold text-text-primary">{row.label}</div>
      <div className="mt-1 type-meta text-text-secondary">
        {CLASSIFICATION_LABEL[row.classification]}
      </div>
      <dl className="mt-1.5 space-y-0.5 type-meta">
        <div className="flex items-center justify-between gap-4">
          <dt className="text-text-muted">Point estimate</dt>
          <dd className="tabular-nums text-text-primary">{fmt(metric, row.point)}</dd>
        </div>
        <div className="flex items-center justify-between gap-4">
          <dt className="text-text-muted">P10–P90</dt>
          <dd className="tabular-nums text-text-primary">
            {fmt(metric, row.p10)} – {fmt(metric, row.p90)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * Word-wrapping category-axis tick.
 *
 * The default recharts category tick renders the label on a SINGLE <text> line,
 * which clips long pathway labels (e.g. "Phase 2 Reciprocating Engine Jun 2029")
 * to "Phase 2 Recipr…" once columns crowd. We render our own tick that wraps the
 * label onto up to THREE lines on word boundaries so the FULL label is readable
 * without overflowing into a neighbouring column. No truncation / ellipsis.
 */
const MAX_TICK_LINES = 3;
/** Approx chars that fit on one tick line at fontSize 11. Wraps, never clips. */
const TICK_LINE_CHARS = 14;

function wrapTickLabel(label: string): string[] {
  const words = label.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > TICK_LINE_CHARS && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }
  if (current) lines.push(current);
  // Keep the label whole: if it would exceed the line cap, merge the overflow
  // back onto the last allowed line rather than dropping words (no data loss).
  if (lines.length > MAX_TICK_LINES) {
    const head = lines.slice(0, MAX_TICK_LINES - 1);
    const tail = lines.slice(MAX_TICK_LINES - 1).join(' ');
    return [...head, tail];
  }
  return lines;
}

function WrappingTick(props: {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
}) {
  const { x = 0, y = 0, payload } = props;
  const value = payload?.value == null ? '' : String(payload.value);
  const lines = wrapTickLabel(value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="middle"
        fill="var(--text-secondary)"
        fontSize={11}
        dy={30}
      >
        {lines.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? 0 : 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

export function PathwayBandChart({
  data,
  metric: controlledMetric,
  onMetricChange,
  heightPx = 256,
}: PathwayBandChartProps) {
  const [internalMetric, setInternalMetric] = useState<BandMetric>('lcoe');
  const metric = controlledMetric ?? internalMetric;

  const setMetric = (next: BandMetric) => {
    if (controlledMetric === undefined) setInternalMetric(next);
    onMetricChange?.(next);
  };

  const rows = useMemo(() => buildRows(data, metric), [data, metric]);

  const presentClassifications = useMemo(() => {
    const seen = new Set<PathwayClassification>();
    rows.forEach((r) => seen.add(r.classification));
    return [...seen];
  }, [rows]);

  if (data.length === 0) {
    return null;
  }

  const axisFormatter = (v: number) =>
    metric === 'lcoe' ? `$${Math.round(v)}` : `$${Math.round(v / 1_000_000)}M`;

  const ariaSummary = rows
    .map(
      (r) =>
        `${r.label}: ${fmt(metric, r.point)} (P10 ${fmt(metric, r.p10)}, P90 ${fmt(metric, r.p90)})`,
    )
    .join('; ');

  return (
    <div data-testid="pathway-band-chart" className="w-full">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium text-text-secondary">
          {metric === 'lcoe' ? 'LCOE confidence band (real $/MWh)' : 'NPV confidence band'}
        </span>
        <div
          className="flex items-center gap-1"
          role="group"
          aria-label="Band metric"
          data-testid="pathway-band-metric-toggle"
        >
          {TOGGLE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              aria-pressed={metric === opt.value}
              onClick={() => setMetric(opt.value)}
              className={`rounded-md px-2 py-0.5 type-meta border ${
                metric === opt.value
                  ? 'selected-accent'
                  : 'border-border text-text-secondary hover:bg-state-hover'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0 ? (
        <div
          className="rounded-lg border border-border bg-surface-0 p-3 text-xs text-text-muted"
          data-testid="pathway-band-chart-empty"
        >
          No finite {metric === 'lcoe' ? 'LCOE' : 'NPV'} estimates to plot.
        </div>
      ) : (
        <div
          style={{ height: heightPx }}
          role="img"
          aria-label={`${metric === 'lcoe' ? 'LCOE' : 'NPV'} P10–P90 confidence bands by pathway. ${ariaSummary}`}
          data-testid="pathway-band-chart-canvas"
        >
          <SafeResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={rows}
              margin={{ top: 12, right: 16, left: 8, bottom: 28 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
              {/* Custom wrapping tick — shows the FULL pathway label across up to
                  three lines instead of clipping it to "…". `height` reserves
                  room for the wrapped lines so they don't collide with the axis. */}
              <XAxis dataKey="label" tick={<WrappingTick />} stroke="var(--border)" interval={0} height={60} />
              <YAxis tickFormatter={axisFormatter} tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} stroke="var(--border)" width={56} />
              {metric === 'npv' ? <ReferenceLine y={0} stroke="var(--border)" /> : null}
              <Tooltip
                content={<BandTooltip metric={metric} />}
                cursor={{ fill: 'var(--state-hover)' }}
              />
              {/* Invisible anchor bar carries the deterministic point + the
                  P10..P90 ErrorBar whisker. We render a thin coloured bar so
                  each column reads as a categorical marker without implying the
                  point is a "magnitude from zero" for NPV (which can be negative). */}
              <Bar dataKey="point" name={metric === 'lcoe' ? 'LCOE' : 'NPV'} barSize={10} radius={[2, 2, 2, 2]} isAnimationActive={false}>
                {rows.map((r) => (
                  <Cell key={r.pathwayId} fill={CLASSIFICATION_COLORS[r.classification]} />
                ))}
                <ErrorBar
                  dataKey="error"
                  width={8}
                  strokeWidth={2}
                  stroke="var(--text-secondary)"
                  direction="y"
                />
              </Bar>
              <Scatter
                dataKey="point"
                name="Point estimate"
                isAnimationActive={false}
                shape={(props: any) => {
                  const { cx, cy, payload } = props;
                  if (cx == null || cy == null) return <g />;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={3.5}
                      fill={CLASSIFICATION_COLORS[(payload as ChartRow).classification]}
                      stroke="var(--surface-1)"
                      strokeWidth={1}
                    />
                  );
                }}
              />
            </ComposedChart>
          </SafeResponsiveContainer>
        </div>
      )}

      {presentClassifications.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-3" data-testid="pathway-band-legend">
          {presentClassifications.map((c) => (
            <span key={c} className="flex items-center gap-1 text-[10px] text-text-muted">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: CLASSIFICATION_COLORS[c] }}
                aria-hidden="true"
              />
              {CLASSIFICATION_LABEL[c]}
            </span>
          ))}
        </div>
      ) : null}

      {/* Off-screen accessible data table mirroring the chart for SR users. */}
      <table className="sr-only" data-testid="pathway-band-table">
        <caption>{metric === 'lcoe' ? 'LCOE' : 'NPV'} confidence bands by pathway</caption>
        <thead>
          <tr>
            <th>Pathway</th>
            <th>Point</th>
            <th>P10</th>
            <th>P90</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.pathwayId}>
              <td>{r.label}</td>
              <td>{fmt(metric, r.point)}</td>
              <td>{fmt(metric, r.p10)}</td>
              <td>{fmt(metric, r.p90)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
